// @ts-check

import { BOX_INDEX, PEERS_BY_CELL } from "../engine/topology.js";

export class SudokuBoard {
  constructor(container, handlers) {
    this.container = container;
    this.handlers = handlers;
    this.cells = [];
    this.grid = document.createElement("div");
    this.grid.className = "sudoku-grid";
    this.grid.setAttribute("role", "grid");
    this.grid.setAttribute("aria-label", "Sudoku board, 9 rows and 9 columns");
    this.grid.setAttribute("aria-rowcount", "9");
    this.grid.setAttribute("aria-colcount", "9");
    for (let row = 0; row < 9; row += 1) {
      const rowElement = document.createElement("div");
      rowElement.className = "sudoku-row";
      rowElement.setAttribute("role", "row");
      for (let column = 0; column < 9; column += 1) {
        const cell = row * 9 + column;
        const element = document.createElement("div");
        element.className = "sudoku-cell";
        element.dataset.cell = String(cell);
        element.setAttribute("role", "gridcell");
        element.setAttribute("aria-rowindex", String(row + 1));
        element.setAttribute("aria-colindex", String(column + 1));
        element.tabIndex = cell === 0 ? 0 : -1;
        element.innerHTML = `<span class="cell-value"></span><span class="cell-notes" aria-hidden="true">${Array.from({ length: 9 }, (_, index) => `<span data-note="${index + 1}"></span>`).join("")}</span><span class="cell-marker" aria-hidden="true"></span>`;
        element.addEventListener("click", () => this.handlers.select(cell));
        element.addEventListener("keydown", (event) =>
          this.onKeyDown(event, cell),
        );
        rowElement.append(element);
        this.cells.push(element);
      }
      this.grid.append(rowElement);
    }
    container.replaceChildren(this.grid);
  }

  render(game, settings, visibleCells) {
    const selected = game.selected_cell;
    const selectedValue = game.values[selected];
    const hintCells = new Set(game.hint?.affected_cells ?? []);
    for (let cell = 0; cell < 81; cell += 1) {
      const element = this.cells[cell];
      const cellView = visibleCells[cell];
      const value = cellView.value === null ? "0" : String(cellView.value);
      const given = cellView.given;
      const peer = PEERS_BY_CELL[selected].includes(cell);
      const matching =
        settings.highlightMatchingDigits &&
        selectedValue !== "0" &&
        value === selectedValue;
      element.className = "sudoku-cell";
      element.classList.toggle("given", given);
      element.classList.toggle("selected", selected === cell);
      element.classList.toggle("peer", peer);
      element.classList.toggle("matching", matching);
      element.classList.toggle(
        "conflict",
        settings.showConflicts && cellView.conflicts.length > 0,
      );
      element.classList.toggle(
        "mismatch",
        cellView.matches_checked_solution === false,
      );
      element.classList.toggle("hint-evidence", hintCells.has(cell));
      element.classList.toggle(
        "box-edge-right",
        cell % 3 === 2 && cell % 9 !== 8,
      );
      element.classList.toggle(
        "box-edge-bottom",
        Math.floor(cell / 9) % 3 === 2 && Math.floor(cell / 9) !== 8,
      );
      element.querySelector(".cell-value").textContent =
        value === "0" ? "" : value;
      const notes = cellView.notes;
      for (const note of element.querySelectorAll("[data-note]")) {
        const noteElement = /** @type {HTMLElement} */ (note);
        noteElement.textContent = notes.includes(
          Number(noteElement.dataset.note),
        )
          ? noteElement.dataset.note
          : "";
      }
      element.classList.toggle("has-value", value !== "0");
      element.tabIndex = selected === cell ? 0 : -1;
      element.setAttribute("aria-selected", String(selected === cell));
      element.setAttribute(
        "aria-label",
        cellLabel(
          cell,
          value,
          notes,
          given,
          cellView.conflicts.length > 0,
          cellView.matches_checked_solution === false,
        ),
      );
    }
  }

  focus(cell) {
    this.cells[cell]?.focus();
  }

  onKeyDown(event, cell) {
    let destination;
    if ((event.ctrlKey || event.metaKey) && event.key === "Home")
      destination = 0;
    else if ((event.ctrlKey || event.metaKey) && event.key === "End")
      destination = 80;
    else if (event.key === "ArrowLeft" || event.key.toLowerCase() === "a")
      destination = cell % 9 === 0 ? cell : cell - 1;
    else if (event.key === "ArrowRight" || event.key.toLowerCase() === "d")
      destination = cell % 9 === 8 ? cell : cell + 1;
    else if (event.key === "ArrowUp" || event.key.toLowerCase() === "w")
      destination = Math.max(0, cell - 9);
    else if (event.key === "ArrowDown" || event.key.toLowerCase() === "s")
      destination = Math.min(80, cell + 9);
    else if (event.key === "Home") destination = Math.floor(cell / 9) * 9;
    else if (event.key === "End") destination = Math.floor(cell / 9) * 9 + 8;
    else if (
      /^[1-9]$/.test(event.key) &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      this.handlers.digit(Number(event.key));
      return;
    } else if (
      event.key === "0" ||
      event.key === "Backspace" ||
      event.key === "Delete"
    ) {
      event.preventDefault();
      this.handlers.erase();
      return;
    } else if (event.key.toLowerCase() === "n") {
      event.preventDefault();
      this.handlers.notes();
      return;
    } else if (
      (event.key.toLowerCase() === "u" && !event.ctrlKey && !event.metaKey) ||
      ((event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "z" &&
        !event.shiftKey)
    ) {
      event.preventDefault();
      this.handlers.undo();
      return;
    } else if (
      (event.key.toLowerCase() === "r" && !event.ctrlKey && !event.metaKey) ||
      ((event.ctrlKey || event.metaKey) &&
        (event.key.toLowerCase() === "y" ||
          (event.key.toLowerCase() === "z" && event.shiftKey)))
    ) {
      event.preventDefault();
      this.handlers.redo();
      return;
    } else if (event.key.toLowerCase() === "h") {
      event.preventDefault();
      this.handlers.hint();
      return;
    } else if (event.key === "Escape" || event.key.toLowerCase() === "p") {
      event.preventDefault();
      this.handlers.pause();
      return;
    } else return;
    event.preventDefault();
    this.handlers.select(destination);
    this.focus(destination);
  }
}

function cellLabel(cell, value, notes, given, conflict, mismatch) {
  const parts = [`Row ${Math.floor(cell / 9) + 1}, column ${(cell % 9) + 1}`];
  if (value === "0") parts.push("empty");
  else parts.push(given ? `given ${value}` : `player-entered ${value}`);
  if (notes.length)
    parts.push(
      `candidates ${new Intl.ListFormat("en").format(notes.map(String))}`,
    );
  if (conflict) parts.push("conflict in row, column, or box");
  if (mismatch) parts.push("does not match the checked solution");
  parts.push(`box ${BOX_INDEX[cell] + 1}`);
  return `${parts.join(". ")}.`;
}
